import React from "react";
import { Users, FileEdit, TrendingUp, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useWallet } from "../../context/WalletContext";

// New component for animated heading
const AnimatedHeading = ({ text }) => {
  const letters = Array.from(text);

  const containerVariants = {
    hidden: { opacity: 1 }, // Start visible
    hover: (i = 1) => ({
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 * i },
    }),
  };

  const childVariants = {
    hidden: {
      opacity: 1,
      y: 0,
    },
    hover: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
  };

  return (
    <motion.h2
      className="text-3xl md:text-4xl font-bold leading-tight mb-4 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 bg-clip-text text-transparent cursor-pointer"
      variants={containerVariants}
      initial="hidden"
      whileHover="hover"
      custom={1}
    >
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          variants={childVariants}
          className="inline-block"
          whileHover={{ 
            scale: 1.2, 
            color: "#4F46E5",
            transition: { 
              type: "spring",
              stiffness: 400,
              damping: 10
            }
          }}
        >
          {letter === " " ? "\u00A0" : letter}
        </motion.span>
      ))}
    </motion.h2>
  );
};

function RolesDashboard() {
  const { account, innovatorProfile } = useWallet();

  const roles = [
    {
      title: "Creators",
      description:
        "Create projects with customized tokenomics, define milestones and tasks, and drive innovation through decentralized collaboration.",
      icon: <FileEdit size={24} className="text-white" />,
      color: "from-indigo-500 to-purple-600",
      role: "creator",
    },
    {
      title: "Contributors",
      description:
        "Build and grow projects by completing tasks, earning ownership (IUs), and shaping the future of innovation.",
      icon: <Users size={24} className="text-white" />,
      color: "from-blue-500 to-indigo-600",
      role: "contributor",
    },
    {
      title: "Investors",
      description:
        "Fund early innovation by investing capital and sharing in project success through Innovation Units ownership.",
      icon: <TrendingUp size={24} className="text-white" />,
      color: "from-purple-500 to-pink-600",
      role: "investor",
    },
    {
      title: "Stakers",
      description:
        "Fuel project growth by signaling trust and boosting reward emissions through their stake.",
      icon: <Shield size={24} className="text-white" />,
      color: "from-indigo-500 to-blue-600",
      role: "staker",
    },
  ];

  // Filter out roles based on user's profile
  const filteredRoles = roles.filter(role => {
    // If no wallet is connected or no profile, show all roles
    if (!account || !innovatorProfile) return true;
    
    // Filter out roles that the user already has
    return !innovatorProfile.roles?.includes(role.role);
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="py-16 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <AnimatedHeading text="Unlocking Collective Innovation Value" />
          <motion.div 
            className="h-1 w-20 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 mx-auto rounded-full"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-gray-600 max-w-2xl mx-auto mt-4 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1 }}
          >
            {account && innovatorProfile ? (
              <>
                Discover new roles to expand your impact in the innovation ecosystem.
                {innovatorProfile.roles?.length > 0 && (
                  <span className="block mt-2 text-sm text-indigo-600">
                    Your current roles: {innovatorProfile.roles.join(", ")}
                  </span>
                )}
              </>
            ) : (
              "CelystikHub connects each participant in the innovation ecosystem with clear roles and aligned incentives"
            )}
          </motion.p>
        </motion.div>


        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredRoles.map((role, idx) => (
            <motion.div
              key={idx}
              className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-md transition-all hover:shadow-lg hover:border-indigo-200"
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="flex p-6">
                {/* Icon */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${role.color} flex items-center justify-center shadow-md mr-4`}>
                  {role.icon}
                </div>

                {/* Text */}
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {role.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {role.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default RolesDashboard;
